/**
 * Theme store for the PWA.
 * Manages theme mode (light/dark/custom), custom colors, and presets.
 * Persists to localStorage and applies CSS variables to the document root.
 */

import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'custom';

export interface CustomThemeColors {
  accent: string;
  background: string;
  text: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  colors: CustomThemeColors;
}

/** Curated theme presets for quick selection. */
export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default-dark',
    name: 'Midnight',
    colors: { accent: '#6366f1', background: '#0a0a0a', text: '#e5e5e5' },
  },
  {
    id: 'default-light',
    name: 'Clean White',
    colors: { accent: '#6366f1', background: '#ffffff', text: '#171717' },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: { accent: '#0ea5e9', background: '#0c1222', text: '#e0f2fe' },
  },
  {
    id: 'forest',
    name: 'Forest',
    colors: { accent: '#22c55e', background: '#0a1a0f', text: '#dcfce7' },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    colors: { accent: '#f59e0b', background: '#1a0f05', text: '#fef3c7' },
  },
  {
    id: 'lavender',
    name: 'Lavender',
    colors: { accent: '#a78bfa', background: '#130f1a', text: '#ede9fe' },
  },
  {
    id: 'rose',
    name: 'Rose',
    colors: { accent: '#f43f5e', background: '#1a0a10', text: '#ffe4e6' },
  },
  {
    id: 'slate',
    name: 'Slate',
    colors: { accent: '#64748b', background: '#0f172a', text: '#e2e8f0' },
  },
];

interface ThemeState {
  mode: ThemeMode;
  customTheme: CustomThemeColors;
  setMode: (mode: ThemeMode) => void;
  setCustomTheme: (colors: Partial<CustomThemeColors>) => void;
  applyPreset: (preset: ThemePreset) => void;
  restore: () => void;
}

const STORAGE_KEY = 'anna-theme';
const STORAGE_KEY_CUSTOM = 'anna-theme-custom';

function adjustBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + Math.round(2.55 * percent)));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + Math.round(2.55 * percent)));
  const b = Math.min(255, Math.max(0, (num & 0xff) + Math.round(2.55 * percent)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function adjustOpacity(hex: string, opacity: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function applyThemeVars(mode: ThemeMode, customTheme: CustomThemeColors) {
  const root = document.documentElement;

  if (mode === 'light') {
    root.style.setProperty('--bg-primary', '#ffffff');
    root.style.setProperty('--bg-secondary', '#f5f5f5');
    root.style.setProperty('--bg-tertiary', '#e5e5e5');
    root.style.setProperty('--text-primary', '#171717');
    root.style.setProperty('--text-secondary', '#525252');
    root.style.setProperty('--text-muted', '#a3a3a3');
    root.style.setProperty('--border-color', '#e5e5e5');
    root.style.setProperty('--accent', '#6366f1');
    root.style.setProperty('--accent-soft', 'rgba(99, 102, 241, 0.1)');
    root.setAttribute('data-theme', 'light');
  } else if (mode === 'dark') {
    root.style.setProperty('--bg-primary', '#0a0a0a');
    root.style.setProperty('--bg-secondary', '#171717');
    root.style.setProperty('--bg-tertiary', '#262626');
    root.style.setProperty('--text-primary', '#e5e5e5');
    root.style.setProperty('--text-secondary', '#a3a3a3');
    root.style.setProperty('--text-muted', '#737373');
    root.style.setProperty('--border-color', '#262626');
    root.style.setProperty('--accent', '#6366f1');
    root.style.setProperty('--accent-soft', 'rgba(99, 102, 241, 0.15)');
    root.setAttribute('data-theme', 'dark');
  } else {
    // Custom theme
    root.style.setProperty('--bg-primary', customTheme.background);
    root.style.setProperty('--bg-secondary', adjustBrightness(customTheme.background, 10));
    root.style.setProperty('--bg-tertiary', adjustBrightness(customTheme.background, 20));
    root.style.setProperty('--text-primary', customTheme.text);
    root.style.setProperty('--text-secondary', adjustOpacity(customTheme.text, 0.7));
    root.style.setProperty('--text-muted', adjustOpacity(customTheme.text, 0.4));
    root.style.setProperty('--border-color', adjustBrightness(customTheme.background, 15));
    root.style.setProperty('--accent', customTheme.accent);
    root.style.setProperty('--accent-soft', `${customTheme.accent}26`);
    root.setAttribute('data-theme', 'custom');
  }
}

const DEFAULT_CUSTOM: CustomThemeColors = {
  accent: '#6366f1',
  background: '#0a0a0a',
  text: '#e5e5e5',
};

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: 'dark',
  customTheme: DEFAULT_CUSTOM,

  setMode: (mode) => {
    set({ mode });
    localStorage.setItem(STORAGE_KEY, mode);
    applyThemeVars(mode, get().customTheme);
  },

  setCustomTheme: (colors) => {
    const updated = { ...get().customTheme, ...colors };
    set({ customTheme: updated, mode: 'custom' });
    localStorage.setItem(STORAGE_KEY, 'custom');
    localStorage.setItem(STORAGE_KEY_CUSTOM, JSON.stringify(updated));
    applyThemeVars('custom', updated);
  },

  applyPreset: (preset) => {
    set({ customTheme: preset.colors, mode: 'custom' });
    localStorage.setItem(STORAGE_KEY, 'custom');
    localStorage.setItem(STORAGE_KEY_CUSTOM, JSON.stringify(preset.colors));
    applyThemeVars('custom', preset.colors);
  },

  restore: () => {
    const saved = (localStorage.getItem(STORAGE_KEY) as ThemeMode) || 'dark';
    let custom = DEFAULT_CUSTOM;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CUSTOM);
      if (raw) custom = JSON.parse(raw);
    } catch {
      // corrupted, use defaults
    }
    set({ mode: saved, customTheme: custom });
    applyThemeVars(saved, custom);
  },
}));
